import { Children, cloneElement, forwardRef, isValidElement, ReactElement, useImperativeHandle, useRef } from 'react';
import { createPortal } from 'react-dom';
import cm from 'classnames';

import { useMergedRefs } from 'components/_hooks/use-merged-refs';
import { getTooltipPortalEl } from './tooltip-portal';
import { useTooltip } from './_use-tooltip';

import styles from './_tooltip.module.scss';
import { isTouchDevice } from 'helpers/_helpers';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

type TooltipSmartProps = {
  className?: string;
  position?: TooltipPosition;
  dynamicPosition?: boolean;
  children: ReactElement | (ReactElement | false)[];
  showTooltip?: boolean;
  tip?: string | ReactElement;
};

const IS_TOUCH = isTouchDevice();

export const Tooltip = forwardRef<HTMLDivElement, TooltipSmartProps>(
  ({ children, className, showTooltip = true, position = 'top', tip, dynamicPosition }, forwardedRef) => {
    const localRef = useRef<HTMLElement | null>(null);
    const tipRef = useRef<HTMLDivElement | null>(null);
    const isTooltip = !!(showTooltip && tip);
    const host = getTooltipPortalEl();

    useImperativeHandle(forwardedRef, () => localRef.current as HTMLDivElement, []);
    const { visible, actualPosition, onEnter, onLeave, onClickCapture } = useTooltip(
      localRef,
      tipRef,
      position,
      dynamicPosition
    );

    const childRef =
      isValidElement(children) && typeof (children as { ref?: unknown }).ref !== 'boolean'
        ? (children as { ref?: React.Ref<HTMLDivElement> }).ref
        : undefined;
    const mergedRef = useMergedRefs(localRef, childRef);

    const renderChild = () => {
      const childrenCount = Children.count(children);

      if (childrenCount === 1 && isValidElement(children)) {
        const child = children as ReactElement;
        if (!isTooltip) return children;
        return cloneElement(child, {
          ref: mergedRef,
          className: cm(child.props.className, className),
          onPointerDown: (e: PointerEvent) => {
            child.props.onPointerDown?.(e);
            if (IS_TOUCH) onEnter();
          },
          onPointerUp: (e: PointerEvent) => {
            child.props.onPointerUp?.(e);
            if (IS_TOUCH) onLeave();
          },
          onClickCapture: (e: MouseEvent) => {
            child.props.onClickCapture?.(e);
            if (IS_TOUCH) onClickCapture(e);
          },
          onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
            child.props.onMouseEnter?.(e);
            if (!IS_TOUCH) onEnter();
          },
          onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
            child.props.onMouseLeave?.(e);
            if (!IS_TOUCH) onLeave(e);
          },
        });
      }
      if (!isTooltip) return <div className={className}>{children}</div>;

      return (
        <div
          ref={localRef as React.RefObject<HTMLDivElement>}
          onClickCapture={IS_TOUCH ? onLeave : undefined}
          onPointerDown={IS_TOUCH ? onEnter : undefined}
          onPointerUp={IS_TOUCH ? onLeave : undefined}
          onMouseEnter={!IS_TOUCH ? onEnter : undefined}
          onMouseLeave={!IS_TOUCH ? (e) => onLeave(e) : undefined}
          className={className}
        >
          {children}
        </div>
      );
    };

    if (!host) return children;

    return (
      <>
        {renderChild()}
        {isTooltip &&
          createPortal(
            <div className={cm(styles.Tooltip, { [styles['Tooltip--visible']]: visible })} ref={tipRef}>
              <div className={cm(styles.Tooltip__tip, styles[`Tooltip__tip--${actualPosition}`])}>{tip}</div>
            </div>,
            host
          )}
      </>
    );
  }
);
